/* eslint-disable no-console */

import * as React from 'react';

import useSWR, {unstable_serialize} from 'swr';

import type {Key, KeyedMutator, SWRConfiguration} from 'swr';

import HttpStatusCode from '@deezer/next/services/types/HttpStatusCode';

export type OnceCallback = (errorCode?: number) => Promise<void> | void;

export type AnalyticsSectionState<T> = {
	data: T | undefined;
	isLoading: boolean;
	isError: boolean;
	isValidating: boolean;
	isRefreshing: boolean;
	mutate: KeyedMutator<T>;
};

type UseAnalyticsParams<T> = {
	key: Key | null;
	fetcher: () => Promise<T>;
	initialData?: T;
	onForbidden?: OnceCallback;
	onError?: OnceCallback;
	config?: SWRConfiguration<T>;
};

type AnalyticsLifecycleState = {
	keyId: string | null;
	hasErrored: boolean;
	keyChangedPending: boolean;
};

type CallbackState = {
	keyId: string | null;
	forbiddenCalled: boolean;
	errorCalled: boolean;
};

type AnalyticsError = {
	message?: unknown;
	name?: unknown;

	response?: {
		status?: unknown;
		headers?: unknown;
		data?: any;
	};

	request?: {
		responseURL?: unknown;
	};

	config?: {
		method?: unknown;
		baseURL?: unknown;
		url?: unknown;
		params?: unknown;
		data?: unknown;

		headers?: {
			Authorization?: unknown;
			authorization?: unknown;

			common?: {
				Authorization?: unknown;
				authorization?: unknown;
			};

			[key: string]: unknown;
		};
	};
};

const LOG_PREFIX = '[useAnalytics]';

const ANALYTICS_DEDUPING_INTERVAL = 1500;

const ANALYTICS_FOCUS_THROTTLE_INTERVAL = 5000;

const inFlightAnalyticsRequests = new Map<string, Promise<unknown>>();

const safeJson = (value: unknown) => {
	try {
		return JSON.stringify(value);
	} catch {
		return String(value);
	}
};

const normalizeResponseData = (data: any) => {
	if (typeof data === 'string') {
		try {
			return JSON.parse(data);
		} catch {
			return data;
		}
	}

	return data;
};

const extractSWRKey = (key: Key | null) => {
	if (!key) {
		return null;
	}

	if (typeof key === 'string') {
		return key;
	}

	if (Array.isArray(key)) {
		return key;
	}

	return String(key);
};

const getStableKeyId = (key: Key | null): string | null => {
	if (!key) {
		return null;
	}

	const serializedKey = unstable_serialize(key);

	return serializedKey || null;
};

const getDedupedRequest = <T>(
	keyId: string,
	fetcher: () => Promise<T>,
): Promise<T> => {
	const existingRequest = inFlightAnalyticsRequests.get(keyId) as
		Promise<T> | undefined;

	if (existingRequest) {
		return existingRequest;
	}

	let request: Promise<T>;

	try {
		request = Promise.resolve(fetcher());
	} catch (error) {
		return Promise.reject(error);
	}

	inFlightAnalyticsRequests.set(keyId, request);

	const clearRequest = () => {
		if (inFlightAnalyticsRequests.get(keyId) === request) {
			inFlightAnalyticsRequests.delete(keyId);
		}
	};

	void request.then(clearRequest, clearRequest);

	return request;
};

const getErrorDetails = (caughtError: unknown) => {
	const error = caughtError as AnalyticsError;

	const message = error?.message;

	const name = error?.name;

	const httpStatus = error?.response?.status;

	const responseHeaders = error?.response?.headers;

	const responseData = normalizeResponseData(error?.response?.data);

	const apiStatus = responseData?.errors?.[0]?.status;

	const status = Number(
		typeof httpStatus !== 'undefined' ? httpStatus : apiStatus,
	);

	const errorCode = responseData?.errors?.[0]?.code;

	const method = error?.config?.method;

	const baseURL = error?.config?.baseURL;

	const url = error?.config?.url ?? error?.request?.responseURL;

	const params = error?.config?.params;

	const dataSent = error?.config?.data;

	const headers = error?.config?.headers;

	const headerKeys = headers ? Object.keys(headers) : [];

	const authHeader =
		headers?.Authorization ??
		headers?.authorization ??
		headers?.common?.Authorization ??
		headers?.common?.authorization;

	return {
		name,
		message,
		status,
		httpStatus,
		apiStatus,
		errorCode,
		method,
		baseURL,
		url,
		params,
		dataSent,
		headerKeys,
		hasAuthorizationHeader: Boolean(authHeader),
		responseHeaders,
		responseData,
	};
};

export function useAnalytics<T>({
	key,
	fetcher,
	initialData,
	onForbidden,
	onError,
	config,
}: UseAnalyticsParams<T>): AnalyticsSectionState<T> {
	const keyId = React.useMemo(() => getStableKeyId(key), [key]);

	const keyLabel = React.useMemo(() => extractSWRKey(key), [key]);

	const [lifecycleState, setLifecycleState] =
		React.useState<AnalyticsLifecycleState>(() => ({
			keyId,
			hasErrored: false,
			keyChangedPending: false,
		}));

	let currentLifecycleState = lifecycleState;

	if (lifecycleState.keyId !== keyId) {
		const nextState: AnalyticsLifecycleState = {
			keyId,
			hasErrored: false,
			keyChangedPending: true,
		};
		setLifecycleState(nextState);
		currentLifecycleState = nextState;
	}

	const callbackStateRef = React.useRef<CallbackState>({
		keyId,
		forbiddenCalled: false,
		errorCalled: false,
	});

	const stableFetcher = React.useCallback((): Promise<T> => {
		if (!keyId) {
			return fetcher();
		}

		return getDedupedRequest(keyId, fetcher);
	}, [fetcher, keyId]);

	const markSuccess = React.useCallback(() => {
		setLifecycleState(current => {
			if (current.keyId !== keyId) {
				return current;
			}

			if (!current.keyChangedPending) {
				return current;
			}

			return {
				...current,
				keyChangedPending: false,
			};
		});
	}, [keyId]);

	const handleAnalyticsError = React.useCallback(
		(caughtError: unknown) => {
			setLifecycleState(current => {
				if (current.keyId !== keyId) {
					return current;
				}

				if (current.hasErrored) {
					return current;
				}

				return {
					...current,
					hasErrored: true,
				};
			});

			const callbackState = callbackStateRef.current;

			if (callbackState.keyId !== keyId) {
				callbackState.keyId = keyId;

				callbackState.forbiddenCalled = false;

				callbackState.errorCalled = false;
			}

			const {
				name,
				message,
				status,
				httpStatus,
				apiStatus,
				errorCode,
				method,
				baseURL,
				url,
				params,
				dataSent,
				headerKeys,
				hasAuthorizationHeader,
				responseHeaders,
				responseData,
			} = getErrorDetails(caughtError);

			if (process.env.NODE_ENV !== 'production') {
				console.log(`${LOG_PREFIX} error ::`, keyLabel, {
					name,
					message,
					status,
					httpStatus,
					apiStatus,
					errorCode,
					method,
					baseURL,
					url,
					params,

					dataSent:
						typeof dataSent === 'string' ? dataSent : safeJson(dataSent),

					headerKeys,
					hasAuthorizationHeader,
					responseHeaders,
					responseData,
				});
			}

			if (
				status === HttpStatusCode.FORBIDDEN &&
				onForbidden &&
				!callbackState.forbiddenCalled
			) {
				callbackState.forbiddenCalled = true;

				void onForbidden(errorCode);

				return;
			}

			if (onError && !callbackState.errorCalled) {
				callbackState.errorCalled = true;

				void onError(errorCode);
			}
		},
		[keyId, keyLabel, onError, onForbidden],
	);

	const isPaused = React.useCallback(() => {
		if (currentLifecycleState.hasErrored) {
			return true;
		}

		return config?.isPaused?.() ?? false;
	}, [config, currentLifecycleState.hasErrored]);

	const {data, error, isLoading, isValidating, mutate} = useSWR<T, any>(
		key,
		key ? stableFetcher : null,
		{
			fallbackData: initialData,
			revalidateOnFocus: false,
			revalidateOnReconnect: false,
			revalidateIfStale: false,

			shouldRetryOnError: false,
			focusThrottleInterval: ANALYTICS_FOCUS_THROTTLE_INTERVAL,
			dedupingInterval: ANALYTICS_DEDUPING_INTERVAL,
			...config,
			isPaused,
			onSuccess: (result, swrKey, swrConfig) => {
				try {
					config?.onSuccess?.(result, swrKey, swrConfig);
				} finally {
					markSuccess();
				}
			},

			onError: (caughtError, swrKey, swrConfig) => {
				try {
					config?.onError?.(caughtError, swrKey, swrConfig);
				} finally {
					handleAnalyticsError(caughtError);
				}
			},
		},
	);

	const initialLoading =
		Boolean(key) && isLoading && typeof data === 'undefined';

	const validatingWithData =
		Boolean(key) &&
		typeof data !== 'undefined' &&
		isValidating &&
		!initialLoading &&
		!currentLifecycleState.hasErrored;

	const isRefreshing =
		validatingWithData && currentLifecycleState.keyChangedPending;

	return {
		data,
		isLoading: initialLoading,
		isError: Boolean(error) && typeof data === 'undefined',
		isValidating:
			Boolean(key) && isValidating && !currentLifecycleState.hasErrored,
		isRefreshing,
		mutate,
	};
}
